import { useMemo } from "react";
import { useUserData } from "../../../hooks/useUserData";
import {
  parsePermissions,
  hasPermission,
  getAllPermissionKeys,
  isSystemAdministratorRole,
  getSessionRoleName,
} from "../../../utils/permissionUtils";
import { organizationHasCapaModule } from "../../../utils/orgModuleAccess";

export { organizationHasCapaModule };

/** My CAPA Tasks — available to all org users when CAPA is enabled; not a role permission. */
export function canAccessCapaMyWork({ userData, isSystemAdmin }) {
  return isSystemAdmin || organizationHasCapaModule(userData);
}

function normalizeStaffId(id) {
  return String(id || "")
    .trim()
    .toLowerCase()
    .replace(/[{}]/g, "");
}

/** Staff id of the user who created the CAPA row. */
export function getCapaCreatorStaffId(capaRow) {
  if (!capaRow) return "";
  return normalizeStaffId(
    capaRow.CreatedByStaffId ??
      capaRow.createdByStaffId ??
      capaRow.CreatedBy ??
      capaRow.createdBy,
  );
}

export function isCapaCreator(capaRow, userData) {
  const creatorId = getCapaCreatorStaffId(capaRow);
  const staffId = normalizeStaffId(userData?.StaffId);
  return creatorId !== "" && creatorId === staffId;
}

/** CAPA owner (workflow driver) — not a role permission. */
export function isCapaOwner(capaRow, userData) {
  if (!capaRow || !userData?.StaffId) return false;
  const ownerId = normalizeStaffId(capaRow.OwnerStaffId ?? capaRow.ownerStaffId);
  const staffId = normalizeStaffId(userData.StaffId);
  return ownerId !== "" && ownerId === staffId;
}

/** Stage transitions — CAPA owner or System Administrator only. */
export function canChangeCapaStage({ capaRow, userData, isSystemAdmin }) {
  if (isSystemAdmin) return true;
  if (!capaRow) return false;
  return isCapaOwner(capaRow, userData);
}

/** Assigned staff can act on their task row (no role permission). */
export function isCapaTaskAssignee(taskRow, userData) {
  if (!taskRow || !userData?.StaffId) return false;
  const assigneeId = normalizeStaffId(
    taskRow.OwnerStaffId ??
      taskRow.ownerStaffId ??
      taskRow.ApproverStaffId ??
      taskRow.approverStaffId ??
      taskRow.AssignedStaffId ??
      taskRow.assignedStaffId,
  );
  const staffId = normalizeStaffId(userData.StaffId);
  return assigneeId !== "" && assigneeId === staffId;
}

/** Update/delete — System Administrator or CAPA creator only (not assignable permissions). */
export function canUpdateCapaRecord({ capaRow, userData, isSystemAdmin }) {
  if (isSystemAdmin) return true;
  if (!capaRow) return false;
  return isCapaCreator(capaRow, userData);
}

export function canDeleteCapaRecord({ capaRow, userData, isSystemAdmin }) {
  if (isSystemAdmin) return true;
  if (!capaRow) return false;
  return isCapaCreator(capaRow, userData);
}

/** Shared CAPA permission checks (align with constants/permissions.js). */
export function useCapaAccess() {
  const { userData } = useUserData();
  const isSystemAdmin = isSystemAdministratorRole(getSessionRoleName(userData));
  const perms = useMemo(
    () =>
      isSystemAdmin
        ? getAllPermissionKeys()
        : parsePermissions(userData?.Permission || ""),
    [isSystemAdmin, userData?.Permission],
  );
  const canViewCapa = isSystemAdmin || hasPermission(perms, "VIEW_CAPA");
  const canAddCapa = isSystemAdmin || hasPermission(perms, "ADD_CAPA");
  const canViewRemovedCapa =
    isSystemAdmin || hasPermission(perms, "VIEW_REMOVED_CAPA");
  const canCapaReports = isSystemAdmin || hasPermission(perms, "CAPA_REPORTS");
  const canCapaAuditLog =
    isSystemAdmin || hasPermission(perms, "CAPA_AUDIT_LOG");
  const canCapaMyWork = canAccessCapaMyWork({ userData, isSystemAdmin });
  const canCapaConfigure =
    isSystemAdmin || hasPermission(perms, "CAPA_CONFIGURE");

  return {
    userData,
    isSystemAdmin,
    canViewCapa,
    canAddCapa,
    canViewRemovedCapa,
    canCapaReports,
    canCapaAuditLog,
    canCapaMyWork,
    canCapaConfigure,
  };
}

